import React, { FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogWindow, DialogWindowClose, useDialogManager } from 'src/components/widgets/dialog';
import { backofficeUrl, T } from 'src/shared/helpers';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import AutoAwesomeIcon from 'src/shared/material_icons/image/auto_awesome.svg';
import InfoIcon from 'src/shared/material_icons/action/info.svg';
import ArrowForwardIcon from 'src/shared/material_icons/navigation/arrow_forward.svg';
import styled from 'styled-components';
import { AI_CREDITS_BILLING_URL } from 'src/components/widgets/ai/aiCreditsBannerContent';

const NO_CREDITS_DIALOG_ID = 'ai-studio-no-credits';

// Same look as the other AI credit dialogs (serviceCreditsAiStudioModal): AI Agents Studio gradient header,
// info box and gradient call to action.
const AI_STUDIO_GRADIENT = 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%)';

// Borderless window so the gradient header can bleed to the rounded top edge; the close icon is white over it.
const NoCreditsWindow = styled(DialogWindow)`
    padding: 0;
    width: 520px;
    flex-basis: 520px;
    max-width: 520px;
    border-radius: 14px;
    overflow: hidden;

    ${DialogWindowClose} {
        color: #ffffff;
        fill: #ffffff;
        right: 20px;
        top: 20px;
    }
`;

const Header = styled.div`
    background: ${AI_STUDIO_GRADIENT};
    color: #ffffff;
    padding: 22px 32px 20px;
`;

const Badge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 255, 255, 0.18);
    border-radius: 20px;
    padding: 5px 12px;
    font-size: 12px;
    font-weight: 600;

    svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
        display: block;
    }
`;

const HeaderTitle = styled.h1`
    margin: 10px 0 0;
    font-size: 22px;
    font-weight: 700;
    color: #ffffff;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 18px 32px 14px;
    background: #ffffff;
`;

const InfoBox = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    border: 1px solid #ece7f7;
    border-radius: 10px;
    padding: 13px 16px;
`;

const InfoIconCircle = styled.div`
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(124, 58, 237, 0.1);
    color: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
        display: block;
    }
`;

const InfoText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-size: 13px;
    line-height: 1.45;
    color: #607d8b;
`;

// What happens after the upgrade, stated under the block: the trial ends, the usage does not stop.
const BillingNote = styled.p`
    margin: 0;
`;

const BillingNoteLink = styled.a`
    color: #7c3aed;
    text-decoration: underline;
`;

const ActionArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 32px 20px;
    background: #ffffff;
    border-top: 1px solid #f1eef8;
`;

const UpgradeButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    border: none;
    border-radius: 10px;
    padding: 14px 20px;
    background: ${AI_STUDIO_GRADIENT};
    color: #ffffff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s ease-in;

    svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
        display: block;
    }

    &:hover {
        opacity: 0.92;
    }
`;

export interface FreeAICreditsUsedUp {
    /** Why the AI tools are blocked: a trial or a Shopify development store with its free AI credits used up. */
    message: string;
    isShopifyDevStore: boolean;
    /** The Upgrade of the trial banner is available: a trial with subscription permissions, Shopify included. */
    canUpgradeTrial: boolean;
    /** A trial whose user cannot manage the subscription: the owner has to upgrade. */
    needsSubscriptionOwner: boolean;
    /** A Shopify trial: the upgrade subscribes the current plan again on Shopify and ends the trial right away. */
    endsShopifyTrial: boolean;
    /** The trial upgrade checkout of the trial banner (subscriptions.tsx, onClickSubscribe); for a Shopify trial, the
     * same plan approved again on Shopify with no trial days. */
    upgradeTrial: () => void;
}

/**
 * NO_CREDITS with the AI credit model: there are no credit packs to buy. Every account gets a one-time amount of
 * free AI credits, and only a trial or a Shopify development store is stopped when they are used up (a paying seller
 * keeps using the tools and pays for them). The dialog and the AI assistant card use this same explanation.
 * A trial outside Shopify gets the same Upgrade of the trial banner in My Account > Subscription, which leads to
 * the checkout. A Shopify trial subscribes its current plan again on Shopify with no trial days, and its trial ends
 * once Shopify approves the charge. A development store has nothing to upgrade here.
 */
/** Why the AI tools stopped: a development store has no trial to upgrade, so it is told something else. */
const creditsUsedUpMessage = (isShopifyDevStore: boolean) =>
    isShopifyDevStore
        ? T._("You've used all the free AI credits available for development stores, so AI features are now paused.", 'Admin')
        : T._(
              "You've used all the free AI credits included in your trial. AI features are paused for you and your customers until you upgrade to a paid plan.",
              'Admin'
          );

/**
 * Upgrade of a Shopify trial: the current plan goes again to SubscribeShopify, with endTrial so the charge has no trial
 * days and the trial ends once Shopify approves it. Shopify then brings the seller back to this page.
 */
const subscribeShopifyEndingTrial = (planID: number) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/Subscription/SubscribeShopify';
    const fields: Record<string, string> = { planID: String(planID), endTrial: 'true', returnUrl: window.location.href };
    Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
};

export function useFreeAICreditsUsedUp(): FreeAICreditsUsedUp {
    const { subscription, ecommerceName, shopifyEcommerceSettings, sellerMarketplace } = useSubscriptionData();
    const { isAllowed } = usePermissions();
    const navigate = useNavigate();

    const isShopify = ecommerceName === 'SHOPIFY';
    // Same rule as the backend: a Shopify development store in every environment.
    const isShopifyDevStore = isShopify && shopifyEcommerceSettings?.isTestStore === true;
    const canManageSubscription = isAllowed(['SUBSCRIPTION']);
    // Same conditions of the Upgrade button of the trial banner (subscriptions.tsx): a Shopify trial
    // upgrades from here too, through the same checkout. Only a development store has nowhere to go.
    const canUpgradeTrial = canManageSubscription && sellerMarketplace !== 'MICROSOFT_AZURE';
    // A Shopify subscription stays active during its trial: it is in trial until its trial end (same rule as the backend).
    const endsShopifyTrial =
        isShopify && !isShopifyDevStore && !!subscription?.planId && !!subscription.trialEnd && new Date(subscription.trialEnd).getTime() > Date.now();

    return {
        message: creditsUsedUpMessage(isShopifyDevStore),
        isShopifyDevStore,
        canUpgradeTrial,
        needsSubscriptionOwner: !canManageSubscription,
        endsShopifyTrial,
        upgradeTrial: () => {
            if (endsShopifyTrial && subscription) subscribeShopifyEndingTrial(subscription.planId);
            else if (sellerMarketplace === 'HP') window.open(backofficeUrl('/Seller/UpgradeTrialSubscription'), '_blank');
            else navigate(backofficeUrl('/Seller/UpgradeTrialSubscription'));
        }
    };
}

interface AIStudioNoCreditsDialogProps {
    onClose: () => void;
    /** Dialog heading. Defaults to the generic out-of-credits copy; callers can override it to fit the context. */
    title?: string;
}

/** Shown when the backend reports NO_CREDITS: the free AI credits of a trial or a development store are used up. */
const AIStudioNoCreditsDialog: FC<AIStudioNoCreditsDialogProps> = ({ onClose, title }) => {
    const { message, isShopifyDevStore, canUpgradeTrial, endsShopifyTrial, upgradeTrial } = useFreeAICreditsUsedUp();

    const onUpgrade = () => {
        onClose();
        upgradeTrial();
    };

    return (
        <Dialog windowDecorator={NoCreditsWindow} onClose={onClose}>
            <Header>
                <Badge>
                    <AutoAwesomeIcon />
                    {T._('AI credits', 'Admin')}
                </Badge>
                <HeaderTitle>{title ?? T._('Free AI credits used up', 'Admin')}</HeaderTitle>
            </Header>
            <Body>
                <InfoBox>
                    <InfoIconCircle>
                        <InfoIcon />
                    </InfoIconCircle>
                    <InfoText>
                        <BillingNote>{message}</BillingNote>
                        {/* A development store has no billing period to be billed at the end of, and
                            nothing to upgrade here: its plan is changed on Shopify. */}
                        {isShopifyDevStore ? (
                            <>
                                <BillingNote>
                                    {T._('To keep using them, upgrade your store to a paid Shopify plan from your Shopify admin.', 'Admin')}
                                </BillingNote>
                                <BillingNote>
                                    <BillingNoteLink href={AI_CREDITS_BILLING_URL} target='_blank' rel='noopener noreferrer'>
                                        {T._('How AI credits work', 'Admin')}
                                    </BillingNoteLink>
                                </BillingNote>
                            </>
                        ) : (
                            <BillingNote>
                                {endsShopifyTrial
                                    ? T._(
                                          'Continuing will end your free trial now and switch you to your paid plan right away: Shopify will ask you to approve the charge. After that, AI usage is billed with your plan.',
                                          'Admin'
                                      )
                                    : T._('After upgrading, AI usage is billed at the end of your billing period.', 'Admin')}{' '}
                                <BillingNoteLink href={AI_CREDITS_BILLING_URL} target='_blank' rel='noopener noreferrer'>
                                    {T._('How AI credits work', 'Admin')}
                                </BillingNoteLink>
                            </BillingNote>
                        )}
                    </InfoText>
                </InfoBox>
            </Body>
            {!isShopifyDevStore && canUpgradeTrial && (
                <ActionArea>
                    <UpgradeButton type='button' onClick={onUpgrade}>
                        {T._('Upgrade', 'Admin')}
                        <ArrowForwardIcon />
                    </UpgradeButton>
                </ActionArea>
            )}
        </Dialog>
    );
};

/** Returns a stable callback that opens the AI Studio no-credits dialog with an optional custom title. */
export function useAIStudioCreditsDialog(): { showNoCreditsDialog: (title?: string) => void } {
    const { showDialog, closeDialog } = useDialogManager();

    const showNoCreditsDialog = useCallback(
        (title?: string) => {
            showDialog(NO_CREDITS_DIALOG_ID, <AIStudioNoCreditsDialog title={title} onClose={() => closeDialog(NO_CREDITS_DIALOG_ID)} />);
        },
        [showDialog, closeDialog]
    );

    return { showNoCreditsDialog };
}
